import configPreferences from "csi/config/preferences";
import ExtendedPath from "csi/core/ExtendedPath";
import Key from "csi/core/Key";
import Spec from "csi/core/Spec";
import def from "csi/def";
import { PreferencePath } from "csi/def/PreferencePath";
import locale from "csi/locale";
import { getSpecsForKeys } from "csi/middleware/attributeRule/RuleService";
import { getDomainClassKeys, OptionalAttributesMaps } from "csi/middleware/attributes/attributeKeysService";
import { ATTR_IMAGES } from "csi/model/image";
import PreferenceView from "csi/prefs/PreferenceView";
import ReflectionClass from "csi/reflection/ReflectionClass";
import {
	ATTR_MODIFIED_AT,
	ATTR_MODIFIED_BY,
	PATH_PARENT,
	TYPE_LOOKUP_ITEM
} from "csi/reflection/reflectionConstants";
import { resolveNodes } from "csi/service/nodeService";
import xml from "csi/xml";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { each, keyBy, map } from "lodash";
import { IAggregateStoreDefinitionInfo } from "src/c8-platform/type/aggregateStore";
import { ICustomViewFilterSettings } from "src/c8-platform/type/customView";
import { ATTR_PARENT, C8AttributeId, C8NodeType } from "src/c8-platform/type/node";
import CustomViewAttributeContainer from "./attributes/CustomViewAttributeContainer";

export type CustomViewOptionalAttributesMaps = OptionalAttributesMaps;

export interface ICustomViewUtil<T> {
	/**
	 * function to handle the options change when switch between custom views
	 * @param options new options
	 * @returns 
	 */
	onChangeOptions: (options: T) => void;
}

export const CustomViewAttributes = {
	ATTRIBUTES: "Attributes",
	DESCRIPTION: "Description",
	IS_SHARED: "IsShared",
	KEYS: "Keys",
	NESTED_VIews: "NestedViews",
	OPTIONS: "Options",
	PAGE_SIZE: "PageSize",
	SOURCE_VIEW: "SourceView",
	TARGET_CLASS: "TargetClass",
};

/** add a path to the given source path if valid and does not duplicate an existing path */
function addPath(sourcePath: ExtendedPath, path: string, resultPathMap: Map<string, ExtendedPath>, ignoreOmit?: boolean, ignoreBadPaths?: boolean): ExtendedPath | undefined {
	const { depth, endType } = sourcePath;
	const resultPath = resultPathMap.get(path);

	// if path already calculated, then return it
	if (!depth && resultPath) {
		return resultPath;
	}

	try {
		const extendedPath = sourcePath.plus(path);
		const { isDisabled, isOmitted, isPathToSecondary, standardPath, value } = extendedPath;
		const existingPath = resultPathMap.get(standardPath);

		if (existingPath) {
			if (value !== existingPath.value) {
				console.warn(`Two typed paths exist for same standard path "${standardPath}": "${value}" and "${existingPath.value}"`);
			} else {
				return extendedPath;
			}
		} else if (extendedPath.backtracks()) {
			// uncomment if debugging backtracks - otherwise ignore paths that backtrack
			//console.warn(`Typed path backtracks: "${value}"`);
		} else if (!isDisabled && (ignoreOmit || !isOmitted)) {
			resultPathMap.set(standardPath, extendedPath);

			// also add path to domain class if this ends at secondary class
			if (isPathToSecondary) {
				const { domainPath } = extendedPath;
				const { standardPath } = domainPath;

				// it's possible the map already has an entry for the standard path, and it is mapped to a different typed path (has abstract classes);
				// so only add if not already in the map
				// Example:
				//		primary: "(Calendar)Child:__Parent__(PurchasedOrderColor)/Child:ProductColor(ProductColor)"
				//		secondary: "(Calendar)Child:__Parent__(PurchasedOrderColor)/Child:ProductColor(Colorway)/Child:Attributes(ColorwayAttributes)"
				if (!resultPathMap.has(standardPath)) {
					resultPathMap.set(domainPath.standardPath, domainPath);
				}
			}

			// omit plural paths - PreferencePath should never be plural; if using within matrix, then need to test only the path within the matrix
			let singlePath = sourcePath;

			if (!sourcePath.isSingle) {
				singlePath = extendedPath.relativeTo(sourcePath);
			}

			if (singlePath.isSingle) {
				return extendedPath;
			}

			console.warn(`PreferencePath cannot be plural: ${sourcePath.endType} + ${path}`);
		}
	} catch (ex) {
		if (!ignoreBadPaths) {
			console.warn(`Typed path does not exist for path "${path}" from type "${endType}"`);
		}
	}
}

/** add class preference paths to given source path, tracking all in the given result map */
function addPreferencePaths(sourcePath: ExtendedPath, preferencePaths: PreferencePath[], resultPathMap: Map<string, ExtendedPath>): void {
	preferencePaths?.forEach(({ bAddTargetPaths, m_path }) => {
		const resultPath = addPath(sourcePath, m_path, resultPathMap);

		if (resultPath && bAddTargetPaths) {
			const preferencePaths = def.getClassPreferencePaths(resultPath.endClass);
			addPreferencePaths(resultPath, preferencePaths, resultPathMap);
		}
	});
}

/**
 * convert Domain object to ComponentItem
 */
export function convertContainerToComponentItem(domainObj: CustomViewAttributeContainer): ComponentItem | undefined {
	if (domainObj) {
		const { name, uniqueId } = domainObj;

		return {
			id: uniqueId,
			name,
		};
	}
}

/** return list of ExtendedPaths available from given source path */
export function getCustomViewAvailablePaths(sourcePath: ExtendedPath, preferencePaths?: PreferencePath[], extraPaths?: Set<string>, extraPathsOptional?: boolean): ExtendedPath[] {
	const resultPathMap = new Map<string, ExtendedPath>();
	const classPreferencePaths = def.getClassPreferencePaths(sourcePath.endClass);

	addPreferencePaths(sourcePath, classPreferencePaths, resultPathMap);
	addPreferencePaths(sourcePath, preferencePaths, resultPathMap);

	if (extraPaths) {
		for (const path of extraPaths) {
			addPath(sourcePath, path, resultPathMap, true, extraPathsOptional);
		}
	}

	// filter out any paths to secondary classes - the paths to their domain classes will already be in the map
	return [...resultPathMap.values()].filter((ep) => !!ep.depth && !ep.isPathToSecondary);
}

/** return list of image labels available from domain class */
export function getCustomViewClassImageLabels(rc: ReflectionClass): ComponentItem[] {
	const { imageLabelEnum } = rc;
	const enumValues = imageLabelEnum?.activeValues ?? [];

	return enumValues.map(({ name }) => ({
		id: name,
		name: locale.getEnumName(xml.enumCreate(imageLabelEnum.name, name)),
	}));
}

/** return list of paths in domain class; if given class is null and (abstract) class is provided instead, infer the domain model from its list of relations */
export function getCustomViewDomainClassPaths(extendedPath: ExtendedPath): ExtendedPath[] {
	const { endClass, endDomainClass } = extendedPath;
	const paths = [extendedPath];

	if (endDomainClass) {
		each(endDomainClass.secondaries, (dest, id) => {
			paths.push(extendedPath.clone().addSecondaryRelationStep(id, dest.type));
		});
	} else {
		endClass.getRelations(true).forEach((rcr) => {
			const dest = rcr.isSecondary ? rcr.getDestinationClass(endClass) : null;
			if (dest) {
				paths.push(extendedPath.clone().addSecondaryRelationStep(rcr.attributeId, dest.type));
			}
		});
	}

	return paths;
}

/** return list of extended paths to parent classes */
export function getCustomViewParentPaths(extendedPath: ExtendedPath): ExtendedPath[] {
	const paths: ExtendedPath[] = [];
	
	try {
		while (true) {
			extendedPath = extendedPath.plus(PATH_PARENT);
			paths.push(extendedPath);
		}
	} catch (ex) {
		// no more parents
	}

	return paths;
}

/** return list of image label keys available from domain class at the end of given path */
export function getCustomViewPathImageLabelKeys(extendedPath: ExtendedPath, matrix?: string): Key[] {
	const { endClass: { imageLabelEnum } } = extendedPath;
	const enumValues = imageLabelEnum?.activeValues ?? [];

	return enumValues.map(({ name }) => new Key({
		id: ATTR_IMAGES,
		index: name,
		extendedPath,
		matrix,
	}));
}

/** return list of keys available from domain class at the end of given path */
export function getCustomViewPathKeys(
	sourcePath: ExtendedPath,
	matrix?: string,
	allowHidden?: boolean,
	allowImageKeys?: boolean,
	omitImages?: boolean,
	optionalAttributesMaps?: CustomViewOptionalAttributesMaps,
	derivedFrom?: ReflectionClass,
	isRelative?: boolean,
): Key[] {
	return getDomainClassKeys(sourcePath, matrix, allowHidden, allowImageKeys, omitImages, optionalAttributesMaps, derivedFrom, isRelative);
}

/** return list of meta data keys available from domain class at the end of given path */
export function getCustomViewPathMetaDataKeys(extendedPath: ExtendedPath, matrix?: string): Key[] {
	return [
		new Key({
			prop: xml.PROP_CREATE_TIME,
			extendedPath,
			matrix,
		}),
		new Key({
			prop: xml.PROP_CREATOR,
			extendedPath,
			matrix,
		}),
		new Key({
			id: ATTR_MODIFIED_AT,
			extendedPath,
			matrix,
		}),
		new Key({
			id: ATTR_MODIFIED_BY,
			extendedPath,
			matrix,
		}),
	];
}

/** return name of container given its extended path */
export function getCustomViewContainerName(loc: ILocaleProvider, extendedPath: ExtendedPath): string {
	// Paths ending at LookupItem use the name of the subtype, rather than the name for the LookupItem class
	const { endDomainType, endRelation, endType } = extendedPath;
	const containerType = endDomainType || endType;
	const typeName = ((containerType === TYPE_LOOKUP_ITEM) && endRelation?.subtypeName) || loc.type(containerType);
	const pathName = configPreferences.getPathName(extendedPath);
	return (pathName && (pathName !== typeName)) ? loc.resource("CustomViewContainerPathConjunction", [typeName, pathName]) : typeName;
}

/** 
 * Return list of singular paths (no plural steps) from given source path which includes any custom view paths as well as any parent paths;
 * not all types include PreferencePaths for parent paths yet (but they probably should), so add them if missing
 */
export function getSingularPathsFromSource(sourcePath: ExtendedPath, omitId?: C8AttributeId): ExtendedPath[] {
	const paths = getCustomViewAvailablePaths(sourcePath).filter(({ startRelationId }) => startRelationId !== omitId);
	const parentPaths = (omitId === ATTR_PARENT) ? [] : getCustomViewParentPaths(sourcePath);
	const destinations = new Set<string>();

	paths.forEach((ep) => {
		destinations.add(ep.endType);
	});

	// add paths to parent classes if there is no other path to that type (Style has shortcuts to parents rather than using __Parent__)
	parentPaths.forEach((ep) => {
		if (!destinations.has(ep.endType)) {
			paths.push(ep);
		}
	});

	return paths;
}

/** populate optional attributes maps for any paths to LookupItem */
export function populateCustomViewOptionalAttributesMaps(maps: CustomViewOptionalAttributesMaps, otherPaths: ExtendedPath[]): void {
	otherPaths.forEach((extendedPath) => {
		const { optionalAttributesMap, value } = extendedPath;
		if (optionalAttributesMap) {
			maps[value] = optionalAttributesMap;
		}
	});
}

/** return attribute specs for specific target class */
export function populateAttributeSpecsFromTargetClassDomain(targetClassNodeType: C8NodeType): Spec[] {
	const keys = populateAttributeKeysFromTargetClassDomain(targetClassNodeType);
	const extendedPath = new ExtendedPath(targetClassNodeType);

	// include revision attribute specs if revision type exist
	const revisionType = extendedPath.endClass.getRevisionType();
	const revisionKeys = populateAttributeKeysFromTargetClassDomain(revisionType);

	const specs = getSpecsForKeys(keys.concat(revisionKeys));
	// eliminate duplicates created from combining specs from the path and specs in view
	const record: Record<string, Spec> = keyBy(specs, (spec) => spec.uniqueId);
	const targetClassSpecs = map(record, (item) => item);
	return targetClassSpecs;
}

/** return attribute keys if target class exist */
export function populateAttributeKeysFromTargetClassDomain(targetClassNodeType: C8NodeType): Key[] {
	let keys = [];
	if (targetClassNodeType) {
		const extendedPath = new ExtendedPath(targetClassNodeType);
		keys = getCustomViewPathKeys(extendedPath, null, true, false, null, null);
		const metaDataKeys = getCustomViewPathMetaDataKeys(extendedPath);
		keys = keys.concat(metaDataKeys);
	}
	return keys;
}

/**
 * retrieve custom view nodes and convert filters to runtime values. Return a record of data store id along with the additional queries associated with the data store
 */
export async function loadDataStoreFilterMapAndPopulateAdditionalQuery(result: IAggregateStoreDefinitionInfo) : Promise<Record<string, ICustomViewFilterSettings>> {
	const dataStoreFilterMap: Record<string, ICustomViewFilterSettings> = {};
	if (result.dataStoreQueryMap) {
		// retrieve and convert filter settings to run time values (numeric keys to actual keys)
		const customViewUrls = Object.values(result.dataStoreQueryMap);
		const customOptions = [
			{ name: "filterSettings", value: {} },
		];

		const customViews = await resolveNodes(customViewUrls);

		Object.entries(result.dataStoreQueryMap).forEach(([key, value]) => {
			const customViewNode = customViews.find((view) => view.$URL === value);

			const preferenceView = new PreferenceView({
				customOptions,
				urlNode: customViewNode.$URL,
				keys: customViewNode.Keys,
			});

			preferenceView.load(customViewNode);
			dataStoreFilterMap[key] = preferenceView.values.filterSettings;
		});
	}
	return dataStoreFilterMap;
}
